import React from 'react';
import {
  FaTachometerAlt,
  FaProjectDiagram,
  FaBuilding,
  FaUsers,
  FaFileAlt,
  FaEye,
  FaKey,
  FaSitemap,
  FaChartBar,
  FaFire,
  FaClock,
} from 'react-icons/fa';

export const NAV_LINKS = {
  dashboard: { id: 'dashboard', label: 'Dashboard', icon: <FaTachometerAlt /> },
  organization: { id: 'organization', label: 'Organizations', icon: <FaBuilding /> },
  users: { id: 'users', label: 'Users', icon: <FaUsers /> },
  pending: { id: 'pending', label: 'Pending Users', icon: <FaClock /> },
  sitecollections: { id: 'sitecollections', label: 'Site Collections', icon: <FaSitemap /> },
  flows: { id: 'flows', label: 'Flows', icon: <FaProjectDiagram /> },
  contents: { id: 'contents', label: 'Contents', icon: <FaFileAlt /> },
  visibility: { id: 'visibility', label: 'Visibility', icon: <FaEye /> },
  apikey: { id: 'apikey', label: 'API Key', icon: <FaKey /> },
  enrollmentkeys: {
    id: 'enrollmentkeys',
    label: 'Enrollment Keys',
    icon: <FaKey />,
    featureKey: 'extension_player'
  },
  metering: { id: 'metering', label: 'Metering & Licensing', icon: <FaChartBar /> }
};

export const ANALYTICS_NAV_LINKS = [
  {
    id: 'survey-insights',
    label: 'Survey Insights',
    icon: <FaChartBar />,
    roles: ['systemadministrator', 'organizationadministrator', 'sitecollectionowner'],
    featureKey: ['survey_module', 'survey_insights']
  },
  {
    id: 'analytics-iap',
    label: 'IAP Analytics',
    icon: <FaChartBar />,
    roles: ['systemadministrator', 'organizationadministrator', 'sitecollectionowner'],
    featureKey: 'advanced_analytics',
    aliases: [
      'advanced-analytics',
      'iap-analytics'
    ]
  },
  {
    id: 'analytics-customer',
    label: 'Behavioural Analytics',
    icon: <FaChartBar />,
    roles: ['systemadministrator', 'organizationadministrator', 'sitecollectionowner'],
    featureKey: ['customer_analytics', 'customer-analytics'],
    aliases: [
      'customer-analytics',
      'behavioural-analytics',
      'behavioral-analytics',
      'journey-mining',
      'friction-signals',
      'session-replay'
    ]
  },
  {
    id: 'heatmap-analytics',
    label: 'Heatmap',
    icon: <FaFire />,
    roles: ['systemadministrator', 'organizationadministrator', 'sitecollectionowner'],
    featureKey: ['heat_map'],
    aliases: [
      'heatmap-settings'
    ]
  }
];

export const ADMIN_LINK_IDS_BY_ROLE = {
  systemadministrator: ['dashboard', 'organization', 'users', 'pending', 'sitecollections', 'flows', 'contents', 'visibility', 'apikey', 'enrollmentkeys', 'metering'],
  organizationadministrator: ['dashboard', 'organization', 'sitecollections', 'users', 'pending', 'flows', 'contents', 'visibility', 'apikey', 'enrollmentkeys', 'metering'],
  sitecollectionowner: ['dashboard', 'organization', 'users', 'sitecollections', 'flows', 'contents', 'visibility', 'apikey'],
  regularuser: ['dashboard', 'organization', 'users', 'sitecollections', 'flows', 'contents', 'visibility'],
  default: ['dashboard']
};

export const ROLE_NAV_PRIORITY = [
  'systemadministrator',
  'organizationadministrator',
  'sitecollectionowner',
  'regularuser'
];

/**
 * Checks whether a given navigation item is entitled and allowed for the given user context.
 *
 * @param {Object} item - Navigation item definition
 * @param {Object} context
 * @param {string[]} [context.roles] - Array of role strings
 * @param {string[]} [context.enabledFeatures] - Array of enabled feature keys from entitlements API
 * @param {boolean} [context.isEntitlementsLoaded=true] - Whether entitlements have finished loading
 * @param {boolean} [context.isSystemAdmin] - Optional override; if omitted, computed from roles
 * @returns {boolean}
 */
export function isNavItemAllowed(item, { roles = [], enabledFeatures = [], isEntitlementsLoaded = true, isSystemAdmin }) {
  if (!item) return false;

  const normalizedRoles = (roles || []).map((r) => String(r || '').toLowerCase());
  const sysAdmin = typeof isSystemAdmin === 'boolean'
    ? isSystemAdmin
    : normalizedRoles.includes('systemadministrator');

  // 1. Role requirement check (if defined on item)
  if (item.roles && Array.isArray(item.roles) && !item.roles.includes('all')) {
    const hasRole = item.roles.some((requiredRole) => normalizedRoles.includes(requiredRole.toLowerCase()));
    if (!hasRole) return false;
  }

  // 2. System Administrator bypasses feature entitlement requirements
  if (sysAdmin) {
    return true;
  }

  // 3. Entitlement Feature Check
  if (item.featureKey) {
    // Fail-closed while entitlements are still loading
    if (!isEntitlementsLoaded) {
      return false;
    }

    const featureKeys = Array.isArray(item.featureKey) ? item.featureKey : [item.featureKey];
    const hasEntitlement = featureKeys.some((key) => enabledFeatures.includes(key));
    if (!hasEntitlement) {
      return false;
    }
  }

  return true;
}

/**
 * Checks whether a given navigation item is permitted based solely on role permissions (RBAC),
 * ignoring entitlement feature keys so that paywalled/gated features remain discoverable in navigation.
 *
 * @param {Object} item - Navigation item definition
 * @param {Object} context
 * @param {string[]} [context.roles] - Array of role strings
 * @param {boolean} [context.isSystemAdmin] - Optional override; if omitted, computed from roles
 * @returns {boolean}
 */
export function isNavItemRoleAllowed(item, { roles = [], isSystemAdmin } = {}) {
  if (!item) return false;

  const normalizedRoles = (roles || []).map((r) => String(r || '').toLowerCase());
  const sysAdmin = typeof isSystemAdmin === 'boolean'
    ? isSystemAdmin
    : normalizedRoles.includes('systemadministrator');

  if (sysAdmin) {
    return true;
  }

  // Role requirement check (if defined on item)
  if (item.roles && Array.isArray(item.roles) && !item.roles.includes('all')) {
    return item.roles.some((requiredRole) => normalizedRoles.includes(requiredRole.toLowerCase()));
  }

  return true;
}
